package top.wcpe.allincore.ui.runtime.theme

import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic

/**
 * 单条 toast 的视觉样式：位置 / 尺寸 / 颜色 / 圆角 / 字号 / 边框 / 阴影 / 动画时长。
 *
 * 由 [ControlTheme.defaultToastStyle] 与 [ControlTheme.toastStyles] 提供。DSL 中
 * `toast(message, style = "myName")` 通过名字从主题表里挑一个 ToastStyle；未指定
 * style 时按 kind 退化到主题的 4 个内建 style（info / success / warning / error），
 * 都没命中则用 [ControlTheme.defaultToastStyle]。
 *
 * 所有字段不可变，由 [Builder] 构造；fluent 风格 getter 与其它主题类一致
 * （`style.width()` 而非 `style.getWidth()`），经 `@get:JvmName` 保留无 `get` 前缀。
 */
@Suppress("MagicNumber", "TooManyFunctions")
class ToastStyle private constructor(
    @get:JvmName("anchor") val anchor: String,
    @get:JvmName("offsetX") val offsetX: Float,
    @get:JvmName("offsetY") val offsetY: Float,
    @get:JvmName("width") val width: Float,
    @get:JvmName("height") val height: Float,
    @get:JvmName("gap") val gap: Float,
    @get:JvmName("backgroundColor") val backgroundColor: Int,
    @get:JvmName("textColor") val textColor: Int,
    @get:JvmName("borderColor") val borderColor: Int,
    @get:JvmName("borderWidth") val borderWidth: Float,
    @get:JvmName("cornerRadius") val cornerRadius: Float,
    @get:JvmName("shadowColor") val shadowColor: Int,
    @get:JvmName("shadowBlur") val shadowBlur: Float,
    @get:JvmName("shadowOffsetX") val shadowOffsetX: Float,
    @get:JvmName("shadowOffsetY") val shadowOffsetY: Float,
    @get:JvmName("textSize") val textSize: Float,
    @get:JvmName("textPaddingX") val textPaddingX: Float,
    @get:JvmName("textPaddingY") val textPaddingY: Float,
    @get:JvmName("fadeInMs") val fadeInMs: Long,
    @get:JvmName("fadeOutMs") val fadeOutMs: Long,
) {

    /**
     * ToastStyle 构建器：复刻 Lombok `@Builder(builderClassName = "Builder")` 的链式 ABI
     * （`ToastStyle.builder().anchor(x)...build()`）。各字段默认值对应原 `@Builder.Default`。
     */
    class Builder internal constructor() {
        private var anchorValue: String = "top"
        private var offsetXValue: Float = 0f
        private var offsetYValue: Float = 16f
        private var widthValue: Float = 240f
        private var heightValue: Float = 32f
        private var gapValue: Float = 6f
        private var backgroundColorValue: Int = 0xFF2196F3.toInt()
        private var textColorValue: Int = 0xFFFFFFFF.toInt()
        private var borderColorValue: Int = 0x00000000
        private var borderWidthValue: Float = 0f
        private var cornerRadiusValue: Float = 2f
        private var shadowColorValue: Int = 0x00000000
        private var shadowBlurValue: Float = 0f
        private var shadowOffsetXValue: Float = 0f
        private var shadowOffsetYValue: Float = 0f
        private var textSizeValue: Float = 1f
        private var textPaddingXValue: Float = 8f
        private var textPaddingYValue: Float = 10f
        private var fadeInMsValue: Long = 150L
        private var fadeOutMsValue: Long = 150L

        fun anchor(value: String): Builder = apply { anchorValue = value }
        fun offsetX(value: Float): Builder = apply { offsetXValue = value }
        fun offsetY(value: Float): Builder = apply { offsetYValue = value }
        fun width(value: Float): Builder = apply { widthValue = value }
        fun height(value: Float): Builder = apply { heightValue = value }
        fun gap(value: Float): Builder = apply { gapValue = value }
        fun backgroundColor(value: Int): Builder = apply { backgroundColorValue = value }
        fun textColor(value: Int): Builder = apply { textColorValue = value }
        fun borderColor(value: Int): Builder = apply { borderColorValue = value }
        fun borderWidth(value: Float): Builder = apply { borderWidthValue = value }
        fun cornerRadius(value: Float): Builder = apply { cornerRadiusValue = value }
        fun shadowColor(value: Int): Builder = apply { shadowColorValue = value }
        fun shadowBlur(value: Float): Builder = apply { shadowBlurValue = value }
        fun shadowOffsetX(value: Float): Builder = apply { shadowOffsetXValue = value }
        fun shadowOffsetY(value: Float): Builder = apply { shadowOffsetYValue = value }
        fun textSize(value: Float): Builder = apply { textSizeValue = value }
        fun textPaddingX(value: Float): Builder = apply { textPaddingXValue = value }
        fun textPaddingY(value: Float): Builder = apply { textPaddingYValue = value }
        fun fadeInMs(value: Long): Builder = apply { fadeInMsValue = value }
        fun fadeOutMs(value: Long): Builder = apply { fadeOutMsValue = value }

        fun build(): ToastStyle = ToastStyle(
            anchorValue,
            offsetXValue,
            offsetYValue,
            widthValue,
            heightValue,
            gapValue,
            backgroundColorValue,
            textColorValue,
            borderColorValue,
            borderWidthValue,
            cornerRadiusValue,
            shadowColorValue,
            shadowBlurValue,
            shadowOffsetXValue,
            shadowOffsetYValue,
            textSizeValue,
            textPaddingXValue,
            textPaddingYValue,
            fadeInMsValue,
            fadeOutMsValue,
        )
    }

    companion object {
        /** 构建器入口，复刻 Lombok 生成的 `ToastStyle.builder()`。 */
        @JvmStatic
        fun builder(): Builder = Builder()
    }
}
